import { AxiosResponse } from 'axios';
import grispiApi from '../../apis/grispiApi';
import { AutocompleteResponseType } from '../../hooks/autocomplete/useAutocompleteSuggestions';
import {
    DetailedUser,
    GroupWithUsers,
    RequesterEditPostData,
    roles,
    SimpleUser,
    UserCreatePostData,
    UserDeleteData,
    UserEditPostData,
    userRolesMap,
    UserRoleUpdatePostData
} from '../../model/types';
import { isAdmin, isAgent, isEndUser } from '../../utils/accountHelper';
import { fetcherWithToken, fetcherWithTokenWithParams } from '../../utils/fetchers';
import { getSafeUserNameForRequest, localizedSearchTermForApi } from '../../utils/helpers';
import endPoints from '../endpoints';

type multipleUserInfoType = 'phone' | 'email';

// to init CurrentUserStore on client-side
export const getUserById = async (userId: number): Promise<DetailedUser> => {
    const data = await fetcherWithToken(`/users/${userId}`);

    return data;
};

export const createUser = async (userCreatePostData: UserCreatePostData): Promise<DetailedUser> => {
    const { role } = userCreatePostData;
    let response: AxiosResponse<DetailedUser>;
    if (role === userRolesMap.ROLE_ADMIN) {
        response = await grispiApi.post(`/admins`, userCreatePostData);
    }
    if (role === userRolesMap.ROLE_AGENT) {
        response = await grispiApi.post(`/agents`, userCreatePostData);
    }
    if (role === userRolesMap.ROLE_END_USER) {
        response = await grispiApi.post('/users', userCreatePostData);
    }
    return response.data;
};

export const updateUserRole = async (postData: UserRoleUpdatePostData): Promise<any> => {
    const { userId } = postData;

    await grispiApi.put(`/users/${userId}/role`, postData);
};

export const updateUser = async (userEditPostData: UserEditPostData | RequesterEditPostData): Promise<any> => {
    if ('roleToChange' in userEditPostData && 'userId' in userEditPostData) {
        const { roleToChange, userId, ...rest } = userEditPostData;
        await updateUserRole({ role: roleToChange, userId });
        return updateUser({ ...rest, role: roleToChange, userId });
    }

    const { userId, role, ...rest } = userEditPostData;
    const postData = { ...rest, userId };

    if (isAdmin(role)) {
        await grispiApi.patch(`/admins/${userId}`, postData);
    }
    if (isAgent(role)) {
        await grispiApi.patch(`/agents/${userId}`, postData);
    }
    if (isEndUser(role)) {
        await grispiApi.patch(`/users/${userId}`, postData);
    }
};
export const updateUserSignature = async (userId: number, signature: { value: string }): Promise<any> => {
    await grispiApi.patch(`/user-settings/${userId}/SIGNATURE`, signature);
};
export const updateOwnSignature = async (signature: { value: string }): Promise<any> => {
    await grispiApi.put(`/user-settings/SIGNATURE`, signature);
};
export const deleteUser = async (data: UserDeleteData): Promise<any> => {
    const { role, id } = data;

    if (isAdmin(role)) {
        await deleteAdmin(id);
    }
    if (isAgent(role)) {
        await deleteAgent(id);
    }
    if (isEndUser(role)) {
        await deleteEndUser(id);
    }
};

const deleteEndUser = async (userId: number): Promise<any> => {
    await grispiApi.delete(`/users/${userId}`);
};

const deleteAgent = async (userId: number): Promise<any> => {
    await grispiApi.delete(`/agents/${userId}`);
};

const deleteAdmin = async (userId: number): Promise<any> => {
    await grispiApi.delete(`/admins/${userId}`);
};

interface mergeUserRequestInterface {
    sourceId: number;
    targetId: number;
}
export const mergeUser = async (data: mergeUserRequestInterface): Promise<any> => {
    await grispiApi.post(`/merge/user`, data);
    // Successful operation will return 200.
    // Source and target users MUST be end users. Other roles cannot be merged.
    // Source will be deleted after this operation.
};

export const sendEmailVerification = async (email: string): Promise<any> => {
    await grispiApi.post(`/email-verification/send-email/${email}`);
};

export const verifyUserInfo = async (param: string, infoType: multipleUserInfoType): Promise<any> => {
    if (infoType === 'email') {
        await verifyEmail(param);
    }
    if (infoType === 'phone') {
        await verifyPhone(param);
    }

    // console.debug('verifyUserInfo: Invalid infoType', infoType);
};
const verifyEmail = async (email: string): Promise<any> => {
    const encodedEmail = encodeURIComponent(email);
    await grispiApi.post(`/emails/verify/${encodedEmail}`);
};
const verifyPhone = async (phone: string): Promise<any> => {
    await grispiApi.post(`/phones/verify/${getSafeUserNameForRequest(phone)}`);
};

export interface IUserForActivationAction {
    userId: number;
    role: roles;
}
export type activationActionOnUser = 'activate' | 'deactivate';
export const activationActionUser = async (
    user: IUserForActivationAction,
    action: activationActionOnUser
): Promise<void> => {
    if (isAdmin(user.role)) {
        if (action === 'activate') {
            await grispiApi.delete(`/admins/${user.userId}/deactivation`);
        }
        if (action === 'deactivate') {
            await grispiApi.post(`/admins/${user.userId}/deactivation`);
        }
    }
    if (isAgent(user.role)) {
        if (action === 'activate') {
            await grispiApi.delete(`/agents/${user.userId}/deactivation`);
        }
        if (action === 'deactivate') {
            await grispiApi.post(`/agents/${user.userId}/deactivation`);
        }
    }
};

export const getGroups = async () => {
    const data: GroupWithUsers[] = await fetcherWithToken('/groups');

    return data;
};

export const getMentionableUsers = async (searchTerm: string) => {
    const data: AutocompleteResponseType<SimpleUser> = await fetcherWithTokenWithParams(
        localizedSearchTermForApi(searchTerm),
        endPoints.autoComplete.mention
    );

    return data;
};

export const addUserToAllGroups = async (userId: number, isAdmin: boolean) => {
    const res: AxiosResponse<number[]> = await grispiApi.put(`/${isAdmin ? 'admins' : 'agents'}/${userId}/groups/all`);
    return res?.data;
};
